import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import Navbar from '../components/Navbar';
import useIsMobile from '../hooks/useIsMobile';

const EMOJI_MAP = {
  "McDonald's": '🍔', 'Taco Bell': '🌮', 'Chipotle': '🌯',
  'Sweetgreen': '🥗', 'Cava': '🧆', 'Just Salad': '🥬',
  'Panera Bread': '🥖', 'Chick-fil-A': '🍗', 'Subway': '🥪',
  'Starbucks': '☕', "Wendy's": '🍟', 'Burger King': '👑',
};

const MODES = [
  { id: 'all', label: 'Show All Items', icon: '≡', desc: 'Browse the full menu' },
  { id: 'protein', label: 'Protein Heroes', icon: '💪', desc: 'Highest protein items' },
  { id: 'calorie', label: 'Calorie Conscious', icon: '📉', desc: 'Under 400 calories' },
  { id: 'efficient', label: 'Most Efficient', icon: '⚡', desc: 'Best protein per calorie' },
  { id: 'make', label: 'Make My Meal', icon: '🎯', desc: 'Custom meal for your goals' },
];

const s = {
  page: { minHeight: '100vh', background: '#ffffff' },
  header: {
    padding: '2rem 2rem 1rem', maxWidth: '1200px', margin: '0 auto',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem',
  },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '1rem' },
  emoji: { fontSize: '2.5rem' },
  restaurantName: { fontFamily: 'Inter, ui-sans-serif, sans-serif', fontWeight: 800, fontSize: '1.8rem', letterSpacing: '-0.02em', color: '#111111' },
  grad: { background: 'linear-gradient(135deg, #3a7d00, #1f4500)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' },
  backBtn: { padding: '0.5rem 1rem', borderRadius: '8px', background: 'transparent', border: '1px solid rgba(0,0,0,0.12)', color: '#666680', fontSize: '0.85rem', cursor: 'pointer' },
  main: { maxWidth: '1200px', margin: '0 auto', padding: '0 2rem 4rem', display: 'grid', gridTemplateColumns: '1fr 300px', gap: '2rem' },
  modesRow: { display: 'flex', gap: '0.75rem', overflowX: 'auto', paddingBottom: '0.5rem', marginBottom: '1.5rem', WebkitOverflowScrolling: 'touch' },
  modeBtn: (active) => ({
    padding: '0.75rem 1rem', borderRadius: '12px', cursor: 'pointer', minWidth: '140px', textAlign: 'center',
    border: active ? '2px solid #3a7d00' : '1px solid rgba(0,0,0,0.08)',
    background: active ? 'rgba(58,125,0,0.08)' : '#f8f8f8',
    color: active ? '#3a7d00' : '#111111', transition: 'all 0.2s',
  }),
  modeIcon: { fontSize: '1.3rem', marginBottom: '0.25rem' },
  modeLabel: { fontWeight: 700, fontSize: '0.85rem' },
  modeDesc: { fontSize: '0.75rem', color: '#666680', marginTop: '2px' },
  filters: { display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '1rem' },
  filterLabel: { color: '#666680', fontSize: '0.8rem', fontWeight: 500 },
  filterBtn: (active) => ({
    padding: '0.35rem 0.75rem', borderRadius: '8px', fontSize: '0.8rem', fontWeight: 500, cursor: 'pointer',
    background: active ? '#3a7d00' : '#f0f0f0',
    border: 'none', color: active ? '#fff' : '#666680', transition: 'all 0.15s',
  }),
  searchBar: {
    width: '100%', padding: '0.75rem 1rem', background: '#f8f8f8',
    border: '1px solid rgba(0,0,0,0.08)', borderRadius: '10px',
    color: '#111111', fontSize: '0.9rem', outline: 'none', marginBottom: '1rem',
  },
  itemCount: { color: '#666680', fontSize: '0.85rem', marginBottom: '1rem' },
  loading: { textAlign: 'center', padding: '4rem', color: '#666680', fontSize: '1rem' },
  grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' },
  card: {
    background: '#f8f8f8', border: '1px solid rgba(0,0,0,0.08)',
    borderRadius: '14px', padding: '1.25rem', transition: 'all 0.2s',
  },
  cardName: { fontFamily: 'Inter, ui-sans-serif, sans-serif', fontWeight: 700, fontSize: '0.9rem', marginBottom: '0.5rem', lineHeight: 1.3, color: '#111111' },
  cardCategory: { fontSize: '0.75rem', color: '#3a7d00', fontWeight: 500, marginBottom: '0.75rem' },
  macroGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '0.75rem' },
  macroCell: { textAlign: 'center', background: '#ffffff', borderRadius: '8px', padding: '0.4rem' },
  macroVal: { fontWeight: 700, fontSize: '0.95rem', color: '#111111' },
  macroLabel: { fontSize: '0.68rem', color: '#666680', textTransform: 'uppercase', letterSpacing: '0.04em' },
  extraRow: { display: 'flex', gap: '0.75rem', marginBottom: '0.75rem' },
  extraCell: { fontSize: '0.78rem', color: '#666680' },
  addBtn: {
    width: '100%', padding: '0.55rem', borderRadius: '8px',
    background: 'linear-gradient(135deg, #3a7d00, #1f4500)',
    color: '#fff', fontWeight: 600, fontSize: '0.82rem', border: 'none', cursor: 'pointer',
  },
  sidebar: { position: 'sticky', top: '80px', alignSelf: 'start' },
  orderBox: { background: '#f8f8f8', border: '1px solid rgba(0,0,0,0.08)', borderRadius: '16px', padding: '1.5rem' },
  orderTitle: { fontFamily: 'Inter, ui-sans-serif, sans-serif', fontWeight: 700, fontSize: '1rem', marginBottom: '1rem', color: '#111111' },
  emptyOrder: { color: '#666680', fontSize: '0.85rem', textAlign: 'center', padding: '1.5rem 0' },
  orderItem: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0', borderBottom: '1px solid rgba(0,0,0,0.06)', fontSize: '0.82rem' },
  removeBtn: { background: 'none', border: 'none', color: '#aaa', cursor: 'pointer', fontSize: '1rem', padding: '0 0.25rem' },
  totals: { marginTop: '1rem', padding: '1rem', background: '#ffffff', borderRadius: '10px', border: '1px solid rgba(0,0,0,0.06)' },
  totalRow: { display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', marginBottom: '0.4rem' },
  clearBtn: { width: '100%', marginTop: '0.75rem', padding: '0.55rem', borderRadius: '8px', background: 'transparent', border: '1px solid rgba(0,0,0,0.12)', color: '#666680', fontSize: '0.82rem', cursor: 'pointer' },
  modal: { position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' },
  modalBox: { background: '#ffffff', border: '1px solid rgba(0,0,0,0.08)', borderRadius: '20px', padding: '2rem', width: '100%', maxWidth: '440px' },
  modalTitle: { fontFamily: 'Inter, ui-sans-serif, sans-serif', fontWeight: 700, fontSize: '1.2rem', marginBottom: '0.5rem', color: '#111111' },
  modalSub: { color: '#666680', fontSize: '0.85rem', marginBottom: '1.5rem' },
  inputGroup: { marginBottom: '1rem' },
  inputLabel: { color: '#666680', fontSize: '0.8rem', fontWeight: 500, marginBottom: '0.35rem', display: 'block' },
  input: { width: '100%', padding: '0.65rem 0.85rem', background: '#f8f8f8', border: '1px solid rgba(0,0,0,0.08)', borderRadius: '8px', color: '#111111', fontSize: '0.9rem', outline: 'none' },
  modalGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' },
  findBtn: { width: '100%', padding: '0.8rem', borderRadius: '10px', background: 'linear-gradient(135deg, #3a7d00, #1f4500)', color: '#fff', fontWeight: 600, fontSize: '0.95rem', marginTop: '1.25rem', border: 'none', cursor: 'pointer' },
  cancelBtn: { width: '100%', padding: '0.7rem', borderRadius: '10px', background: 'transparent', border: '1px solid rgba(0,0,0,0.12)', color: '#666680', fontSize: '0.9rem', marginTop: '0.5rem', cursor: 'pointer' },
};

export default function RestaurantMenu({ user }) {
  const { name } = useParams();
  const navigate = useNavigate();
  const restaurantName = decodeURIComponent(name);
  const emoji = EMOJI_MAP[restaurantName] || '🍽️';
  const isMobile = useIsMobile();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('all');
  const [sort, setSort] = useState('az');
  const [category, setCategory] = useState('All');
  const [search, setSearch] = useState('');
  const [order, setOrder] = useState([]);
  const [showMakeMyMeal, setShowMakeMyMeal] = useState(false);
  const [goals, setGoals] = useState({ maxCal: '', minProtein: '', maxCarbs: '', maxFat: '', maxSodium: '' });

  const CATEGORIES = ['All', ...new Set(items.map(i => i.category).filter(Boolean))];

  useEffect(() => {
    const fetchItems = async () => {
      setLoading(true);
      const { data: restaurant } = await supabase
        .from('restaurants')
        .select('id')
        .eq('name', restaurantName)
        .single();

      if (restaurant) {
        const { data } = await supabase
          .from('menu_items')
          .select('*')
          .eq('restaurant_id', restaurant.id)
          .order('name');
        setItems(data || []);
      }
      setLoading(false);
    };
    fetchItems();
  }, [restaurantName]);

  const getFilteredItems = () => {
    let filtered = [...items];
    if (mode === 'protein') filtered = filtered.sort((a, b) => b.protein - a.protein).slice(0, 20);
    else if (mode === 'calorie') filtered = filtered.filter(i => i.calories < 400);
    else if (mode === 'efficient') filtered = filtered.filter(i => i.calories > 0).sort((a, b) => (b.protein / b.calories) - (a.protein / a.calories));
    else if (mode === 'make') {
      if (goals.maxCal) filtered = filtered.filter(i => i.calories <= parseInt(goals.maxCal));
      if (goals.minProtein) filtered = filtered.filter(i => i.protein >= parseInt(goals.minProtein));
      if (goals.maxCarbs) filtered = filtered.filter(i => i.total_carbs <= parseInt(goals.maxCarbs));
      if (goals.maxFat) filtered = filtered.filter(i => i.total_fat <= parseInt(goals.maxFat));
      if (goals.maxSodium) filtered = filtered.filter(i => i.sodium <= parseInt(goals.maxSodium));
    }
    if (category !== 'All') filtered = filtered.filter(i => i.category === category);
    if (search) filtered = filtered.filter(i => i.name.toLowerCase().includes(search.toLowerCase()));
    if (sort === 'az') filtered = filtered.sort((a, b) => a.name.localeCompare(b.name));
    else if (sort === 'calAsc') filtered = filtered.sort((a, b) => a.calories - b.calories);
    else if (sort === 'calDesc') filtered = filtered.sort((a, b) => b.calories - a.calories);
    else if (sort === 'protein') filtered = filtered.sort((a, b) => b.protein - a.protein);
    return filtered;
  };

  const filtered = getFilteredItems();
  const totals = order.reduce((acc, item) => ({
    calories: acc.calories + (item.calories || 0),
    protein: acc.protein + (item.protein || 0),
    carbs: acc.carbs + (item.total_carbs || 0),
    fat: acc.fat + (item.total_fat || 0),
    sodium: acc.sodium + (item.sodium || 0),
  }), { calories: 0, protein: 0, carbs: 0, fat: 0, sodium: 0 });

  return (
    <div style={s.page}>
      <Navbar user={user} />
      <div style={s.header}>
        <div style={s.headerLeft}>
          <span style={s.emoji}>{emoji}</span>
          <div>
            <div style={{ color: '#666680', fontSize: '0.85rem', marginBottom: '0.2rem' }}>{restaurantName}</div>
            <div style={s.restaurantName}><span style={s.grad}>Macro Guide</span></div>
          </div>
        </div>
        <button style={s.backBtn} onClick={() => navigate('/')}>← All Restaurants</button>
      </div>

      <div style={{ ...s.main, ...(isMobile ? { gridTemplateColumns: '1fr', padding: '0 1rem 4rem' } : {}) }}>
        <div>
          <div style={{ ...s.modesRow, ...(isMobile ? { flexWrap: 'nowrap', overflowX: 'auto' } : {}) }}>
            {MODES.map(m => (
              <div key={m.id} style={{ ...s.modeBtn(mode === m.id), ...(isMobile ? { minWidth: '110px', flexShrink: 0 } : {}) }}
                onClick={() => { setMode(m.id); if (m.id === 'make') setShowMakeMyMeal(true); }}>
                <div style={s.modeIcon}>{m.icon}</div>
                <div style={s.modeLabel}>{m.label}</div>
                <div style={s.modeDesc}>{m.desc}</div>
              </div>
            ))}
          </div>

          <div style={s.filters}>
            <span style={s.filterLabel}>Sort:</span>
            {[['az','A-Z'],['calAsc','Cal ↑'],['calDesc','Cal ↓'],['protein','Protein ↓']].map(([val, label]) => (
              <button key={val} style={s.filterBtn(sort === val)} onClick={() => setSort(val)}>{label}</button>
            ))}
          </div>

          <div style={{ ...s.filters, flexWrap: 'wrap' }}>
            <span style={s.filterLabel}>Category:</span>
            {CATEGORIES.map(c => (
              <button key={c} style={s.filterBtn(category === c)} onClick={() => setCategory(c)}>{c}</button>
            ))}
          </div>

          <input style={s.searchBar} placeholder={`Search ${restaurantName} menu...`} value={search} onChange={e => setSearch(e.target.value)} />

          {loading ? (
            <div style={s.loading}>Loading menu...</div>
          ) : (
            <>
              <div style={s.itemCount}>{filtered.length} items</div>
              <div style={{ ...s.grid, ...(isMobile ? { gridTemplateColumns: '1fr' } : {}) }}>
                {filtered.map(item => (
                  <div key={item.id} style={{ ...s.card, ...(isMobile ? { width: '100%', boxSizing: 'border-box' } : {}) }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = '#3a7d00'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(0,0,0,0.08)'; e.currentTarget.style.transform = 'translateY(0)'; }}>
                    <div style={s.cardName}>{item.name}</div>
                    <div style={s.cardCategory}>{item.category}</div>
                    <div style={{ ...s.macroGrid, gridTemplateColumns: '1fr 1fr', width: '100%' }}>
                      {[['Calories', item.calories],['Protein', `${item.protein}g`],['Carbs', `${item.total_carbs}g`],['Fat', `${item.total_fat}g`]].map(([label, val]) => (
                        <div key={label} style={s.macroCell}>
                          <div style={s.macroVal}>{val}</div>
                          <div style={s.macroLabel}>{label}</div>
                        </div>
                      ))}
                    </div>
                    <div style={s.extraRow}>
                      <span style={s.extraCell}>🧂 {item.sodium}mg sodium</span>
                      <span style={s.extraCell}>🍬 {item.sugars}g sugar</span>
                    </div>
                    <button style={{ ...s.addBtn, width: '100%', boxSizing: 'border-box' }} onClick={() => setOrder(prev => [...prev, item])}>+ Add to Order</button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div style={{ ...s.sidebar, ...(isMobile ? { position: 'static' } : {}) }}>
          <div style={s.orderBox}>
            <div style={s.orderTitle}>🛒 My Order</div>
            {order.length === 0 ? (
              <div style={s.emptyOrder}>
                <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🛒</div>
                <div>Add items to track your macros</div>
              </div>
            ) : (
              <>
                {order.map((item, idx) => (
                  <div key={idx} style={s.orderItem}>
                    <div style={{ flex: 1, paddingRight: '0.5rem', lineHeight: 1.3, color: '#111111' }}>{item.name}</div>
                    <div style={{ color: '#666680', fontSize: '0.78rem', whiteSpace: 'nowrap' }}>{item.calories} cal</div>
                    <button style={s.removeBtn} onClick={() => setOrder(prev => prev.filter((_, i) => i !== idx))}>×</button>
                  </div>
                ))}
                <div style={s.totals}>
                  <div style={{ fontWeight: 700, fontSize: '0.8rem', marginBottom: '0.75rem', color: '#3a7d00' }}>TOTALS</div>
                  {[['Calories', totals.calories],['Protein', `${totals.protein}g`],['Carbs', `${totals.carbs}g`],['Fat', `${totals.fat}g`],['Sodium', `${totals.sodium}mg`]].map(([label, val]) => (
                    <div key={label} style={s.totalRow}>
                      <span style={{ color: '#666680' }}>{label}</span>
                      <span style={{ fontWeight: 600, color: '#111111' }}>{val}</span>
                    </div>
                  ))}
                </div>
                <button style={s.clearBtn} onClick={() => setOrder([])}>Clear Order</button>
              </>
            )}
          </div>
        </div>
      </div>

      {showMakeMyMeal && (
        <div style={s.modal} onClick={e => e.target === e.currentTarget && setShowMakeMyMeal(false)}>
          <div style={s.modalBox}>
            <div style={s.modalTitle}>🎯 Build Your Perfect Meal</div>
            <div style={s.modalSub}>Set your macro goals and we'll filter the menu for you.</div>
            <div style={s.modalGrid}>
              {[['maxCal','Max Calories','e.g. 500'],['minProtein','Min Protein (g)','e.g. 25'],['maxCarbs','Max Carbs (g)','e.g. 40'],['maxFat','Max Fat (g)','e.g. 20'],['maxSodium','Max Sodium (mg)','e.g. 1000']].map(([key, label, ph]) => (
                <div key={key} style={s.inputGroup}>
                  <label style={s.inputLabel}>{label}</label>
                  <input style={s.input} placeholder={ph} value={goals[key]} onChange={e => setGoals(p => ({...p, [key]: e.target.value}))} />
                </div>
              ))}
            </div>
            <button style={s.findBtn} onClick={() => setShowMakeMyMeal(false)}>Find My Meal →</button>
            <button style={s.cancelBtn} onClick={() => { setShowMakeMyMeal(false); setMode('all'); }}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}